import { http } from '@noor/api';
import { env } from '@noor/config';
import {
  SYSTEM_USER_APP,
  systemUserDetailEnvelopeSchema,
  systemUserWriteEnvelopeSchema,
  type SystemUser,
  type SystemUserList,
  type SystemUserSessionList,
  type CreateSystemUserPayload,
  type UpdateSystemUserPayload,
} from '../model/schema';
import {
  toSystemUserList,
  toSystemUserSessionList,
  buildSystemUserListQuery,
  type SystemUserListParams,
} from '../lib/system-user-mappers';

/**
 * Auth-service admin API (`AuthService_*`). Like the session login/refresh,
 * every call below targets the auth host (`VITE_AUTH_API_URL`, paths carry
 * `/api/v1`) — never the main gateway. Responses are parsed at the boundary;
 * every write body carries `{app: 'user'}` (the only served contour).
 */
const authBase = { baseURL: env.VITE_AUTH_API_URL };

const appBody = { app: SYSTEM_USER_APP } as const;

export const systemUserApi = {
  /** Paginated staff identities, newest first (`meta.total` counts all). */
  async list(params: SystemUserListParams): Promise<SystemUserList> {
    const raw = await http.get<unknown>(`/api/v1/identities/${SYSTEM_USER_APP}`, {
      ...authBase,
      query: buildSystemUserListQuery(params),
    });
    return toSystemUserList(raw);
  },

  /** Identity card by user id (status, role, phone, dates; no password_hash). */
  async detail(userId: string): Promise<SystemUser | undefined> {
    const raw = await http.post<unknown>(
      `/api/v1/users/${encodeURIComponent(userId)}/identity`,
      appBody,
      authBase,
    );
    const parsed = systemUserDetailEnvelopeSchema.safeParse(raw);
    return parsed.success ? (parsed.data.data ?? undefined) : undefined;
  },

  /** Login sessions across the identity's devices. */
  async sessions(userId: string, includeRevoked = false): Promise<SystemUserSessionList> {
    const raw = await http.get<unknown>(
      `/api/v1/identities/${SYSTEM_USER_APP}/${encodeURIComponent(userId)}/sessions`,
      {
        ...authBase,
        query: includeRevoked ? { include_revoked: 'true' } : {},
      },
    );
    return toSystemUserSessionList(raw);
  },

  /**
   * Create a staff account — phone plus role plus the employee's name. The
   * password is generated and SMSed to the phone, never returned here.
   */
  async create(payload: CreateSystemUserPayload): Promise<void> {
    const raw = await http.post<unknown>('/api/v1/users', payload, authBase);
    systemUserWriteEnvelopeSchema.parse(raw);
  },

  /**
   * Update a staff account (`PATCH /api/v1/users/{user_id}` — first name,
   * last name, role). Only the keys present in `payload` change; the caller
   * builds it as a diff (see `toUpdateSystemUserPayload`) so untouched
   * fields are never sent. An empty payload is a no-op and is rejected here
   * rather than sent. Unknown `userId` → 404, unknown `role` → 400.
   */
  async update(userId: string, payload: UpdateSystemUserPayload): Promise<void> {
    if (Object.keys(payload).length === 0) return;
    const raw = await http.patch<unknown>(
      `/api/v1/users/${encodeURIComponent(userId)}`,
      payload,
      authBase,
    );
    systemUserWriteEnvelopeSchema.parse(raw);
  },

  /** Ban an identity, blocking further authentication. */
  async ban(userId: string): Promise<void> {
    const raw = await http.post<unknown>(
      `/api/v1/users/${encodeURIComponent(userId)}/ban`,
      appBody,
      authBase,
    );
    systemUserWriteEnvelopeSchema.parse(raw);
  },

  /** Lift a ban, restoring authentication. */
  async unban(userId: string): Promise<void> {
    const raw = await http.post<unknown>(
      `/api/v1/users/${encodeURIComponent(userId)}/unban`,
      appBody,
      authBase,
    );
    systemUserWriteEnvelopeSchema.parse(raw);
  },

  /** Soft-delete an identity (the phone stays taken). */
  async remove(userId: string): Promise<void> {
    const raw = await http.post<unknown>(
      `/api/v1/users/${encodeURIComponent(userId)}/delete`,
      appBody,
      authBase,
    );
    systemUserWriteEnvelopeSchema.parse(raw);
  },

  /**
   * Generate a new password and revoke the identity's sessions; the plaintext
   * goes to the owner by SMS, never back to the operator.
   */
  async resetPassword(userId: string): Promise<void> {
    const raw = await http.post<unknown>(
      `/api/v1/users/${encodeURIComponent(userId)}/reset-password`,
      appBody,
      authBase,
    );
    systemUserWriteEnvelopeSchema.parse(raw);
  },

  /** Revoke a single session, forcing that device to re-authenticate. */
  async revokeSession(sessionId: string): Promise<void> {
    const raw = await http.delete<unknown>(
      `/api/v1/sessions/${encodeURIComponent(sessionId)}`,
      authBase,
    );
    systemUserWriteEnvelopeSchema.parse(raw);
  },

  /** Revoke every session for an identity (admin only). */
  async revokeAllSessions(userId: string): Promise<void> {
    const raw = await http.delete<unknown>(
      `/api/v1/identities/${SYSTEM_USER_APP}/${encodeURIComponent(userId)}/sessions`,
      authBase,
    );
    systemUserWriteEnvelopeSchema.parse(raw);
  },
};
